/**
 * Hybrid Data Loader - Uses API when available, falls back to JSON files
 * Provides seamless migration path from JSON files to database API
 */

import { 
  getSeasonData as apiGetSeasonData,
  getManySeasonData as apiGetManySeasonData,
  getTrackData as apiGetTrackData,
  loadJsonData as apiLoadJsonData
} from '../../../utils/apiClient';
import { apiUrl } from '../../../utils/apiConfig';

/**
 * Direct JSON file loading (replaces dataLoader.jsx dependency)
 */
const loadJsonFile = async (filename) => {
  try {
    const safeName = encodeURIComponent(filename);
    const response = await fetch(`/data/${safeName}`);
    if (!response.ok) {
      throw new Error(`Failed to load ${filename}: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    throw new Error(`Failed to load ${filename}: ${error.message}`);
  }
};

// The season the data is in: the latest one with results (the pipeline's last_race_data.json), not the calendar year.
// They differ from 1 January until the first race of the new season, when the new year has no data at all.
let currentSeasonRequest = null;
let currentSeasonValue = null;

export const getCurrentSeason = () => {
  if (!currentSeasonRequest) {
    currentSeasonRequest = loadJsonFile('last_race_data.json')
      .then((status) => Number(status.last_race_season) || new Date().getFullYear())
      .catch(() => new Date().getFullYear())
      .then((season) => {
        currentSeasonValue = season;
        return season;
      });
  }
  return currentSeasonRequest;
};

// The season once it has been loaded, null before
export const peekCurrentSeason = () => currentSeasonValue;

const buildSeasonRange = async (startYear, minYear = 2016) => {
  const currentYear = await getCurrentSeason();
  const fromYear = Math.max(minYear, startYear || minYear);
  const years = [];

  for (let year = currentYear; year >= fromYear; year -= 1) {
    years.push(year);
  }

  return years;
};

/**
 * JSON fallback functions (replaces dataLoader.jsx imports)
 */
// raceNumber is the highest race to include; null/undefined means the whole season
const jsonGetSeasonData = async (dataType, season, raceNumber = null) => {
  const fileName = dataType === "standings"
    ? `standings_${season}.json`
    : dataType === "race"
    ? `data_${season}.json`
    : (() => { throw new Error(`Unknown dataType: ${dataType}`); })();
  
  const seasonData = await loadJsonFile(fileName);
  return raceNumber == null ? seasonData : seasonData.filter((race) => race.race_number <= raceNumber);
};

const jsonGetManySeasonData = async (dataType, startYear) => {
  const fileNameFor = {
    standings: (year) => `standings_${year}.json`,
    race: (year) => `data_${year}.json`
  }[dataType];
  if (!fileNameFor) {
    throw new Error(`Unknown dataType: ${dataType}`);
  }

  const seasonRange = await buildSeasonRange(startYear);
  const seasons = await Promise.all(seasonRange.map(async (year) => {
    try {
      return await loadJsonFile(fileNameFor(year));
    } catch (e) {
      console.warn(`Skipping missing or failed file: ${fileNameFor(year)}`);
      return [];
    }
  }));
  return seasons.flat();
};

const jsonGetTrackData = async (startYear, track, trackFilterType = 'exact') => {
  const seasonRange = await buildSeasonRange(startYear);
  const similarTracksMap = await loadJsonFile("track_similarity.json");

  const matchesTrack = (race) => {
    const isExact = race.track_name === track;
    const isSimilar = similarTracksMap[track]?.includes(race.track_name);

    if (trackFilterType === "exact") return isExact;
    if (trackFilterType === "similar") return isSimilar;
    if (trackFilterType === "both") return isExact || isSimilar;
    return false;
  };

  const seasons = await Promise.all(seasonRange.map(async (year) => {
    const fileName = `data_${year}.json`;
    try {
      return (await loadJsonFile(fileName)).filter(matchesTrack);
    } catch (e) {
      console.warn(`Skipping missing or failed file: ${fileName}`);
      return [];
    }
  }));
  return seasons.flat();
};

const jsonLoadJsonData = async (filename) => {
  return loadJsonFile(filename);
};

/**
 * API availability cache: the API is assumed to be up, and is only skipped
 * for a short while after a request to it has failed
 */
let apiHealthCache = {
  isAvailable: null,
  lastCheck: 0,
  checkInterval: 30000 // 30 seconds
};

async function isApiAvailable() {
  const recentlyFailed = apiHealthCache.isAvailable === false &&
    (Date.now() - apiHealthCache.lastCheck) < apiHealthCache.checkInterval;
  return !recentlyFailed;
}

/**
 * Hybrid wrapper for loading functions
 */
async function hybridLoader(apiFunction, jsonFunction, ...args) {
  try {
    // First try API
    if (await isApiAvailable()) {
      return await apiFunction(...args);
    }
  } catch (error) {
    console.warn('API loading failed, falling back to JSON:', error.message);
    // Mark API as unavailable temporarily
    apiHealthCache.isAvailable = false;
    apiHealthCache.lastCheck = Date.now();
  }
  
  // Fallback to JSON files
  return await jsonFunction(...args);
}

/**
 * Season/track data is large (1-3 MB per season), so each distinct request is
 * loaded once per page session. Callers get their own copy of the array
 * because many of them sort it in place.
 */
const seasonDataCache = new Map();

const cachedLoad = async (key, load) => {
  if (!seasonDataCache.has(key)) {
    const request = load().catch((error) => {
      seasonDataCache.delete(key);
      throw error;
    });
    seasonDataCache.set(key, request);
  }
  return (await seasonDataCache.get(key)).slice();
};

/**
 * Get season data (race results or standings) with hybrid loading
 * @param {string} dataType - "race" or "standings"
 * @param {number} season - Year
 * @param {number} raceNumber - Optional max race number
 * @param {string} quality - "fantasy", "core", or "full"
 * @returns {Promise<Array>} Season data
 */
export const getSeasonData = async (dataType, season, raceNumber = null, quality = 'core') => {
  return cachedLoad(`season:${dataType}:${season}:${raceNumber}:${quality}`, () => hybridLoader(
    (dt, s, rn, q) => apiGetSeasonData(dt, s, rn, q),
    (dt, s, rn) => jsonGetSeasonData(dt, s, rn), // JSON loader doesn't support quality param
    dataType,
    season,
    raceNumber,
    quality
  ));
};

/**
 * Get multi-season data with hybrid loading
 * @param {string} dataType - "race" or "standings"
 * @param {number} startYear - Earliest year to include
 * @param {string} quality - "fantasy", "core", or "full"
 * @returns {Promise<Array>} Multi-season data
 */
export const getManySeasonData = async (dataType, startYear, quality = 'core') => {
  return cachedLoad(`many:${dataType}:${startYear}:${quality}`, () => hybridLoader(
    (dt, sy, q) => apiGetManySeasonData(dt, sy, q),
    (dt, sy) => jsonGetManySeasonData(dt, sy), // JSON loader doesn't support quality param
    dataType,
    startYear,
    quality
  ));
};

/**
 * Multi-season data straight from the JSON files (no API attempt), for callers that already
 * know the API is unavailable
 */
export const getManySeasonDataFromJson = (dataType, startYear) =>
  cachedLoad(`json-many:${dataType}:${startYear}`, () => jsonGetManySeasonData(dataType, startYear));

/**
 * Get track-specific data with hybrid loading
 * @param {number} startYear - Earliest year to include
 * @param {string} track - Track name
 * @param {string} trackFilterType - "exact", "similar", or "both"
 * @param {string} quality - "fantasy", "core", or "full"
 * @returns {Promise<Array>} Track data
 */
export const getTrackData = async (startYear, track, trackFilterType = 'exact', quality = 'core') => {
  return cachedLoad(`track:${startYear}:${track}:${trackFilterType}:${quality}`, () => hybridLoader(
    (sy, t, tft, q) => apiGetTrackData(sy, t, tft, q),
    (sy, t, tft) => jsonGetTrackData(sy, t, tft), // JSON loader doesn't support quality param
    startYear,
    track,
    trackFilterType,
    quality
  ));
};

/**
 * Career race count for a driver (fast count query); 0 when the API is unavailable
 */
export const getDriverRaceCount = async (driverName, startYear = 2000) => {
  try {
    const response = await fetch(apiUrl(`/drivers/${encodeURIComponent(driverName)}/race-count?startYear=${startYear}`));

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const result = await response.json();
    return result.raceCount || 0;
  } catch (error) {
    console.warn(`Failed to get race count for ${driverName}:`, error.message);
    return 0;
  }
};

/**
 * All races for one driver from the database API, falling back to filtering the season JSON files
 */
export const getDriverSeasonData = async (driverName, startYear = 2000) => {
  try {
    const response = await fetch(apiUrl(`/drivers/${encodeURIComponent(driverName)}/performance?startYear=${startYear}`));

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }

    const result = await response.json();

    if ((result.source === 'database' || result.source === 'cache') && result.data) {
      return result.data;
    }
    throw new Error('API returned fallback data');
  } catch (error) {
    console.warn(`Database API failed for ${driverName}, falling back to JSON files:`, error.message);

    const seasons = await Promise.all((await buildSeasonRange(startYear)).map(async (year) => {
      try {
        const seasonData = await getSeasonData("race", year);
        return seasonData.filter((race) => race.driver_name === driverName);
      } catch (e) {
        console.warn(`Skipping missing or failed data for year ${year}`);
        return [];
      }
    }));
    return seasons.flat().sort((a, b) => a.season_year - b.season_year || a.race_number - b.race_number);
  }
};

/**
 * Load static JSON data with hybrid loading
 * @param {string} filename - Name of the JSON file
 * @returns {Promise<any>} Static data
 */
const staticDataCache = new Map();

export const loadJsonData = (filename) => {
  // Static files are shared by many components: load each one once per page session
  if (!staticDataCache.has(filename)) {
    const request = hybridLoader(
      (fn) => apiLoadJsonData(fn),
      (fn) => jsonLoadJsonData(fn),
      filename
    ).catch((error) => {
      staticDataCache.delete(filename);
      throw error;
    });
    staticDataCache.set(filename, request);
  }
  return staticDataCache.get(filename);
};

/**
 * Preload multiple files using hybrid loading
 * @param {string[]} filenames - Array of filenames to preload
 * @param {Function} onProgress - Optional progress callback
 * @returns {Promise<Array>} Promise resolving to array of results
 */
export const preloadData = async (filenames, onProgress) => {
  let loaded = 0;

  return Promise.all(filenames.map(async (filename) => {
    let result;
    try {
      const data = await loadJsonData(filename);
      result = { filename, data, success: true };
    } catch (error) {
      console.warn(`Failed to preload ${filename}:`, error);
      result = { filename, error, success: false };
    }

    loaded++;
    onProgress?.(loaded, filenames.length, filename);
    return result;
  }));
};

/**
 * Get data loading method being used
 * @returns {Promise<string>} "api" or "json"
 */
export const getDataLoadingMethod = async () => {
  return (await isApiAvailable()) ? 'api' : 'json';
};

/**
 * Force API health check refresh
 */
export const refreshApiHealth = () => {
  apiHealthCache = {
    isAvailable: null,
    lastCheck: 0,
    checkInterval: 30000
  };
};

/**
 * Get API health status
 */
export const getApiHealthStatus = () => {
  return {
    available: apiHealthCache.isAvailable,
    lastCheck: new Date(apiHealthCache.lastCheck).toISOString(),
    nextCheck: new Date(apiHealthCache.lastCheck + apiHealthCache.checkInterval).toISOString()
  };
};

/**
 * Simple cache control functions (no complex caching needed for hybrid loader)
 */
export const clearCache = () => {
  staticDataCache.clear();
  seasonDataCache.clear();
  // Reset API health cache when clearing cache
  apiHealthCache = {
    isAvailable: null,
    lastCheck: 0,
    checkInterval: 30000
  };
};

export const invalidateCache = (filename) => {
  // For hybrid loader, just reset API health to force fresh check
  if (filename) {
    staticDataCache.delete(filename);
    refreshApiHealth();
  }
};

export const getCacheStats = () => {
  return {
    apiHealthCache: apiHealthCache,
    note: 'Hybrid loader uses API-first strategy with minimal caching'
  };
};

export const evictExpiredCache = () => {
  // Reset API health cache if it's old
  const now = Date.now();
  if (now - apiHealthCache.lastCheck > apiHealthCache.checkInterval) {
    refreshApiHealth();
    return 1; // Indicate one cache item was evicted
  }
  return 0;
};

const hybridDataLoader = {
  getSeasonData,
  getManySeasonData,
  getTrackData,
  loadJsonData,
  preloadData,
  getManySeasonDataFromJson,
  getDriverRaceCount,
  getDriverSeasonData,
  getDataLoadingMethod,
  refreshApiHealth,
  getApiHealthStatus
};

export default hybridDataLoader;
